import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, ToastContainer, Spinner, Row, Col, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaSearch, FaEraser } from "react-icons/fa";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast } from "react-toastify";
import "../../Styles/AssetRegister.css";
import ScaleLoader from "react-spinners/ScaleLoader";

const AssetRegister = () => {
    const navigate = useNavigate();
    const customer_currency = (localStorage.getItem("customer_currency") || "").replace(/"/g, "");
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter State
    const [filters, setFilters] = useState({
        asset_type: "",
        asset_name: "",
        asset_description: "",
        asset_date: "",
        asset_amount: ""
    });

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // Main Data Fetching
    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = {
                customer_id: Number(localStorage.getItem("customer_id")) || 1005,
                api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
                language: "en",
                user_id: localStorage.getItem("user_id") || 14857,
                limit: 10,
                page: 1,
                asset_type: filters.asset_type,
                asset_name: filters.asset_name,
                asset_description: filters.asset_description,
                asset_date: filters.asset_date,
                asset_amount: filters.asset_amount ? Number(filters.asset_amount) : ""
            };

            const response = await simplePostCall(ApiConfig.getassets, JSON.stringify(payload));

            if (response && response.result) {
                setAssets(response.data || []);
            } else {
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            toast.error("Failed to load assets");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Delete Logic
    const handleDelete = async (assetId) => {
        if (window.confirm("Are you sure you want to delete this asset?")) {
            try {
                const payload = {
                    customer_id: Number(localStorage.getItem("customer_id")) || 1005,
                    api_key: localStorage.getItem("api_key") || "b9359f494e77b6589ce12dd62518c585",
                    user_id: localStorage.getItem("user_id") || 14857,
                    asset_id: assetId
                };

                // Using delete endpoint from your ApiConfig
                const response = await simplePostCall(ApiConfig.Deleteeassets, JSON.stringify(payload));

                if (response && response.result) {
                    toast.success("Asset deleted successfully");
                    fetchInitialData(); // Refresh list
                } else {
                    toast.error(response.message || "Delete failed");
                }
            } catch (error) {
                console.error("Error deleting asset:", error);
                toast.error("An error occurred during deletion");
            }
        }
    };

    const resetFilters = () => {
        setFilters({
            asset_type: "",
            asset_name: "",
            asset_description: "",
            asset_date: "",
            asset_amount: ""
        });
    };

    return (
     <div>
  <ToastContainer position="top-right" />

  <Container fluid>
    {/* Topbar */}
    <div className="d-flex justify-content-between align-items-center mb-3 bg-white rounded mt-4 p-2">
      <h3>Assets</h3>

      <Link to="/accountant/finance/asset/create" style={{ textDecoration: "none" }}>
        <Button style={{ background: "#53b7e8", border: "none" }}>
          + Add Asset
        </Button>
      </Link>
    </div>

    {/* Filter Section */}
    <div className="p-2 mb-2 mt-2 rounded"
    style={{margin: '0px 0px 0px -23px'}}>
      <Row className="g-2">
        <Col md={2}>
          <Form.Control
            name="asset_type"
            placeholder="Type"
            value={filters.asset_type}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            name="asset_name"
            placeholder="Asset Name"
            value={filters.asset_name}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            name="asset_description"
            placeholder="Description"
            value={filters.asset_description}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            type="date"
            name="asset_date"
            value={filters.asset_date}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2}>
          <Form.Control
            type="number"
            name="asset_amount"
            placeholder="Amount"
            value={filters.asset_amount}
            onChange={handleFilterChange}
          />
        </Col>

        <Col md={2} className="d-flex gap-2">
          <Button
            style={{ background: "#53b7e8", border: "none" }}
            onClick={fetchInitialData}
          >
            <FaSearch />
          </Button>

          <Button variant="secondary" onClick={resetFilters}>
            <FaEraser />
          </Button>
        </Col>
      </Row>
    </div>

    {/* Asset Table */}
    <div className="asset-register-custom">

      {/* Header */}
      <div className="asset-header-row-2 px-2 py-3 mb-2 fw-bold text-muted d-none d-lg-flex">
        <div className="col-asset-1">Sr No</div>
        <div className="col-asset-1">Type</div>
        <div className="col-asset-1">Name</div>
        <div className="col-asset-1">Description</div>
        <div className="col-asset-1">Date</div>
        <div className="col-asset-1">Amount</div>
        <div className="col-asset-1 text-center">Actions</div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="text-center p-2">
          <ScaleLoader color="#F4BB44" />
        </div>
      ) : assets.length > 0 ? (
        assets.map((item, index) => (
          <div
            key={item.asset_id}
            className="asset-row-2 p-2 mb-2 bg-white border rounded d-flex"
          >
            <div className="col-asset-1">{index + 1}</div>
            <div className="col-asset-1">{item.asset_type}</div>
            <div className="col-asset-1 fw-medium">
              {item.asset_name}
            </div>

            <div
              className="col-asset-1 text-truncate"
              title={item.asset_description}
            >
              {item.asset_description}
            </div>

            <div className="col-asset-1">
              {new Date(item.asset_date).toLocaleDateString()}
            </div>

            <div className="col-asset-1">
              {customer_currency}{item.asset_amount}
            </div>

            <div className="col-asset-1 d-flex gap-2 justify-content-center">
              <button
                className="btn btn-sm"
                onClick={() =>
                  navigate(`/accountant/finance/asset/create/${item.asset_id}`)
                }
              >
                <FaEdit />
              </button>

              <button
                className="btn btn-sm"
                onClick={() => handleDelete(item.asset_id)}
              >
                <FaTrashAlt color="red" />
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center p-2 bg-white border rounded">
          No assets match your search.
        </div>
      )}
    </div>
  </Container>
</div>

    );
};

export default AssetRegister;